import React, { useState } from 'react';
import { Box, Stack, useMediaQuery, useTheme } from '@mui/material';
import { CommentThread } from './CommentThread';
import { CommentMobileReplies } from './CommentMobileReplies';
import { CommentInput } from '../Comments/CommentsInput';
import { CommentListSkeleton } from './CommentSkeleton';
import { CommentLoadingSpinner } from './CommentLoadingSpinner';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme as appTheme } from '../../constants/theme';
import { IFeedComment } from '../../redux/activity/types';

interface CommentSectionProps {
    comments?: IFeedComment[];
    onSendComment: (text: string, parent?: IFeedComment) => void;
    onLike: (comment: IFeedComment) => void;
    onEdit?: (comment: IFeedComment, newText: string) => void;
    onDelete?: (comment: IFeedComment) => void;
    hideMainInput?: boolean;
    loadMoreReplies?: (comment: IFeedComment) => void;
    fetchingComments?: boolean;
    fetchingMoreReplies?: boolean;
}

export const CommentSection: React.FC<CommentSectionProps> = ({
    comments,
    onSendComment,
    onLike,
    onEdit,
    onDelete,
    hideMainInput = false,
    loadMoreReplies,
    fetchingComments,
    fetchingMoreReplies,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileReplies, setMobileReplies] = useState<IFeedComment | null>(null);
    const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
    const { themeStyle } = useThemeContext();

    const handleReply = (comment: IFeedComment, replyText: string) => {
        onSendComment(replyText, comment);
        setActiveReplyId(null); // Hide reply input after sending
    };

    const handleShowRepliesMobile = (comment: IFeedComment) => {
        if (isMobile) setMobileReplies(comment);
    };

    const handleEdit = (comment: IFeedComment, newText: string) => {
        if (onEdit) {
            onEdit(comment, newText);
        }
    };

    const handleDelete = (comment: IFeedComment) => {
        if (onDelete) {
            onDelete(comment);
        }
    };

    const handleSetActiveReply = (commentId: string | null) => {
        setActiveReplyId(commentId);
    };

    // Show loading spinner on initial load when no comments exist yet
    const showInitialLoading = fetchingComments && (!comments || comments.length === 0);

    // Show skeleton when we have some comments but still loading (pagination)
    const showLoadingMore = fetchingComments && comments && comments.length > 0;

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {mobileReplies ? (
                <CommentMobileReplies
                    parent={mobileReplies}
                    onBack={() => setMobileReplies(null)}
                    onLike={onLike}
                    onReply={handleReply}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onShowRepliesMobile={handleShowRepliesMobile}
                    isMobile={isMobile}
                    activeReplyId={activeReplyId}
                    onSetActiveReply={handleSetActiveReply}
                    loadMoreReplies={loadMoreReplies}
                    fetchingMoreReplies={fetchingMoreReplies}
                />
            ) : (
                <Stack spacing={2} flex={1} sx={{ overflowY: 'auto', minHeight: 0, pb: 2 }}>
                    {showInitialLoading ? (
                        // Show full loading spinner on initial load
                        <CommentListSkeleton count={2} />
                    ) : (
                        <>
                            {/* Render actual comments */}
                            {!!comments &&
                                comments.map(comment => (
                                    <CommentThread
                                        key={comment._id}
                                        comment={comment}
                                        onLike={onLike}
                                        onReply={handleReply}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                        onShowRepliesMobile={handleShowRepliesMobile}
                                        isMobile={isMobile}
                                        activeReplyId={activeReplyId}
                                        onSetActiveReply={handleSetActiveReply}
                                        loadMoreReplies={comment => {
                                            loadMoreReplies && loadMoreReplies(comment);
                                        }}
                                        fetchingMoreReplies={fetchingMoreReplies}
                                    />
                                ))}

                            {/* Show skeleton for pagination loading. */}
                            {showLoadingMore && <CommentLoadingSpinner message="Loading comments..." />}

                            {/* Show empty state when no comments and not loading */}
                            {!fetchingComments && (!comments || comments.length === 0) && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 4,
                                        color: appTheme[themeStyle].tag_white,
                                        fontStyle: 'italic',
                                        opacity: 0.7,
                                    }}>
                                    No comments yet. Be the first to comment!
                                </Box>
                            )}
                        </>
                    )}
                </Stack>
            )}
            {!hideMainInput && (
                <Box sx={{ mt: 'auto', pt: 2 }}>
                    <CommentInput onCommentSend={text => onSendComment(text, mobileReplies ?? undefined)} />
                </Box>
            )}
        </Box>
    );
};
